import * as React from 'react';
import * as ReactDOM from 'react-dom';
import openIdAuthentication from 'authentication/openIdAuthentication';
import { Provider } from 'mobx-react';
import { Router } from 'react-router-dom';
import httpService from 'stores/http-service';
import { TeamStore as MobxTeamStore } from 'nomst/Stores/team.store';
import Team from 'components/team';
import { history } from 'nomst/Stores/Navigation/router.store';
import { UserDataManager } from 'utils/MobxDataManager/userDataManager';
import { GlobalErrorView } from 'components/ErrorPage/globalErrorView';
import * as singleton from 'singletons';
import * as mobxSingleton from 'utils/MobxDataManager/singletons';
import { EmployeeDataManager } from 'utils/MobxDataManager/employeeDataManager';
import { ManagerDataManager } from 'utils/MobxDataManager/managerDataManager';
import { ConversationDataManager } from 'utils/MobxDataManager/conversationDataManager';
import { PromotionRequestDataManager } from 'utils/MobxDataManager/promotionRequestDataManager';
import { EventDataManager } from 'utils/MobxDataManager/eventDataManager';
import { JobDataManager } from 'utils/MobxDataManager/jobDataManager';
import { PayHistoryDataManager } from 'utils/MobxDataManager/payHistoryDataManager';
import { TeamCycleManager } from 'utils/MobxDataManager/teamCycleManager';
import { AccountDataManager } from 'utils/MobxDataManager/accountDataManager';
import { BudgetServiceManager } from 'utils/MobxDataManager/budgetServiceManager';
import { httpResponseConstants, FeatureFlags, TeamRoutes } from 'typings/enums';
import { FeatureFlag } from 'typings/featureFlag';
import { defaultFlagSettings } from 'utils/featureFlagHelper';
import ConfigurationDataManager from 'utils/MobxDataManager/configurationDataManager';
import * as managerActions from 'nomst/Actions/manager';
import * as actions from 'nomst/Actions/configurations';
import { teamLogger } from 'utils/logger';
import TeamDown from 'nomst/Components/Decision/teamDown';
import './boot.scss';
import DiagnosticsManager from 'utils/MobxDataManager/diagnosticsManager';
import SalesForceDataManager from 'utils/MobxDataManager/salesForceDataManager';
import NotificationDataManager from 'utils/MobxDataManager/notificationDataManager';
import { UserFactory } from 'nomst/Stores/User/userFactory';
import { IUserDto } from 'typings/user';
import { configure } from 'mobx';
import PrintServiceDataManager from 'utils/MobxDataManager/printServiceDataManager';
// This is to configure mobx the same as it was by default in v4/5 so we don't get a bunch of warnings.
// We are now on mobx v6 which has a stricter default configuration
// See https://mobx.js.org/migrating-from-4-or-5
// TODO (COLAB-1477): Update this to use recommended settings
configure({ enforceActions: 'never' });

interface IElement extends Element { }

declare global {
    namespace JSX {
        interface IntrinsicElements {
            'ps-conversation': {
                ref: (element: IElement) => void,
                authority: string,
                clientId: string,
                redirectUri: string,
                userId: string,
                employeeSourceId: string,
                masterAccountId: string,
                entityId: string,
                entityName: string,
                entityType: string,
                unreadMessageCount: number,
                baseEmployeeUrl: string,
                disabled?: boolean,
                hidePeopleInChat?: boolean
            };
            'ps-org': {
                ref: (element: IElement) => void,
                authority: string,
                clientId: string,
                redirectUri: string,
                employeeSourceId: string,
                masterAccountId: string,
                displayedLevelCount: number,
                maxRowsPerLevel: number,
                employeeSortFormat: string,
                isHidden: boolean,
                closeLinkText: string
            };
        }
    }
}

// tslint:disable-next-line:no-any For some reason react router relies on any
const renderApp = (root: React.ReactNode, mobxStore: Nullable<MobxTeamStore>, currentHistory: any) => {
    const appRoot = document.getElementById('team-app');

    if (!appRoot) {
        teamLogger.trackException(new Error('No root element detected'));
        throw Error('No app element');
    }
    ReactDOM.render(
        (
            <Provider store={mobxStore} >
                <Router history={currentHistory}>
                    {root}
                </Router>
            </Provider>
        ),
        appRoot
    );

    fadeInApp(appRoot);
};

// Fades in app on top of splash screen/static content
const fadeInApp = (appRoot: HTMLElement): void => {
    // Ensure splash screen is visible for more than just brief,
    // jarring flash (at least 350ms)
    // (increase delay here if you want to simulate network latency)
    const splashDelay = Math.max(350, 0);

    window.setTimeout(() => {
        // fade in app content on top of static content
        appRoot.classList.add('fade-in');
    }, splashDelay);

    // remove loading content once we're up and running
    window.setTimeout(() => {
        document.body.removeChild(document.getElementById('loadingContent') ?? document.createElement('loadingContent'));
    }, 350);
};

const initGTM = (userInfo: IUserInfo, featureFlags: string[]): void => {
    window.mx = Object.assign(
        {},
        window.mx,
        {
            jsContext: {
                accountId: userInfo.masterAccountId || undefined,
                accountName: userInfo.masterAccountName || undefined,
                userId: userInfo.userId || undefined,
                basicProductName: 'Team',
                email: userInfo.email || undefined,
                salesforceId: userInfo.salesforceId || undefined,
                featureFlag: featureFlags || undefined
            }
        });

    if (window.dataLayer) {
        // Fire off custom event to GTM's dataLayer
        try {
            window.dataLayer.push({event: 'mx-login'});
        } catch (e) {
            teamLogger.trackException(new Error(e));
        }
    }

    // For testing Gainsight
    window?.aptrinsic?.(
        'identify',
        {
            'id': userInfo.userId || undefined,
            'email': userInfo.email || undefined
        },
        {
            'id': userInfo.masterAccountId || undefined,
            'name': userInfo.masterAccountName || undefined
        }
    ); 
};

const getFeatureFlagState = async (): Promise<FeatureFlag[]> => {
    const tasks = Object.keys(FeatureFlags).map(
        async (flagName: keyof typeof FeatureFlags): Promise<FeatureFlag> => {
            return {
                name: flagName,
                key: FeatureFlags[flagName],
                state: await singleton.featureFlagManager.isFeatureFlagOn(FeatureFlags[flagName])
            };
        }
    );

    return Promise.all(tasks);
};

const bootApp = async () => {
    const loading = document.getElementById('loadingContent');
    if (loading) {
        loading.classList.remove('zero-opacity');
    }

    const teamServiceElement = document.getElementById('teamservice');
    if (!teamServiceElement) {
        teamLogger.trackException(new Error('No service element detected'));
        throw Error('teamservice element does not exist');
    }

    const authority = teamServiceElement.dataset.authority;
    const clientId = teamServiceElement.dataset.clientid;
    const launchDarklyId = teamServiceElement.dataset.launchdarklyid;
    const jobCollabBaseUrl = teamServiceElement.dataset.jobcollabredirect;
    const fileExtensions = (teamServiceElement.dataset.uploadfileextensions || '').split(',');
    const fileSizeLimit = teamServiceElement.dataset.uploadfilesizelimit;

    openIdAuthentication.setupOpenId(authority ?? '', clientId ?? '');

    // If someone is refreshing from the error or site down pages let's give them a chance to get unbroken
    // by sending them back to the home page.
    const url = history.location.pathname === TeamRoutes.Error ||
        history.location.pathname === TeamRoutes.TeamDown ||
        history.location.pathname === TeamRoutes.AccountError ? '' :
        `${history.location.pathname}${history.location.search}`;
    history.replace(url);

    let user;
    try {
        user = await openIdAuthentication.signIn(url);
        openIdAuthentication.setupLogoutOtherTab();
    } catch (e) {
        teamLogger.trackException(new Error(e));
        renderApp(<GlobalErrorView logoutOverride={logout} />, null, history);
        history.push(TeamRoutes.Error);
        return;
    }

    if (!user) {
        return;
    }

    httpService.setHeaders('Authorization', `Bearer ${user.access_token}`);
    let userInfo: IUserInfo;
    const userDataManager = new UserDataManager({appState: singleton.appState});
    try {
        userInfo = await userDataManager.getUserInfo() ?? {} as IUserInfo;
    } catch (e) {
        teamLogger.trackException(new Error(e));
        renderApp(<GlobalErrorView logoutOverride={logout} />, null, history);
        history.push(TeamRoutes.InvalidAccount);
        return;
    }

    const masterAccountId = userInfo.masterAccountId;
    const userId = userInfo.userId;
    const roles = userInfo.userRoles;

    teamLogger.setUserAndAccount(userId, masterAccountId);
    singleton.initFeatureFlagManager(userInfo, launchDarklyId ?? '');

    const featureFlags: {[key in FeatureFlags]: boolean|number} = defaultFlagSettings;
    const featureFlagsForGtm: string[] = [];
    (await getFeatureFlagState()).forEach((flag: FeatureFlag) => {
        const flagStateString = flag.state ? 'on' : 'off';
        featureFlags[flag.key] = flag.state;
        featureFlagsForGtm.push(`${flag.name}_${flagStateString}`);
    });

    if (featureFlags[FeatureFlags.TeamDown]) {
        renderApp(<TeamDown />, null, history);
        history.push(TeamRoutes.TeamDown);
        return;
    }

    if (jobCollabBaseUrl && featureFlags[FeatureFlags.JobCollabOnly]) {
        window.location.href = jobCollabBaseUrl;
        return;
    }

    initGTM(userInfo, featureFlagsForGtm);

    const serviceUrlElement = document.getElementById('serviceUrl');
    if (!serviceUrlElement) {
        teamLogger.trackException(new Error('No service url detected'));
        throw Error('serviceUrl element does not exist');
    }

    const employeeServiceUrl = serviceUrlElement.dataset.employee;
    const conversationServiceUrl = serviceUrlElement.dataset.conversation;
    const notificationServiceUrl = serviceUrlElement.dataset.notification;
    const labServiceUrl = serviceUrlElement.dataset.lab ?? '';

    const hasAccessToCurrentPage =
        await openIdAuthentication.authorizePageAccess(roles, history.location.pathname, masterAccountId);
    if (hasAccessToCurrentPage === false) {
        return;
    }

    const appConfiguration = {
        employeeServiceUrl,
        conversationServiceUrl,
        jobCollabBaseUrl
    } as IAppConfiguration;

    const mobxUser = UserFactory.getUser({
        userId,
        roles
    } as IUserDto);

    singleton.init(featureFlags);
    mobxSingleton.initDataManager();

    // TODO: Move the setup into the above function please
    const employeeDataManager = new EmployeeDataManager({ appState: singleton.appState });
    employeeDataManager.setBaseUrl(employeeServiceUrl ?? '');
    mobxSingleton.dataManagerFactory.setEmployeeDataManager(employeeDataManager);
    const managerDataManager = new ManagerDataManager({ appState: singleton.appState });
    mobxSingleton.dataManagerFactory.setManagerDataManager(managerDataManager);
    const conversationDataManager = new ConversationDataManager({ appState: singleton.appState });
    conversationDataManager.setFileVariables(fileExtensions, fileSizeLimit ?? '');
    conversationDataManager.setBaseUrl(conversationServiceUrl ?? '');
    mobxSingleton.dataManagerFactory.setConversationDataManager(conversationDataManager);
    const promotionRequestDataManager = new PromotionRequestDataManager({ appState: singleton.appState });
    mobxSingleton.dataManagerFactory.setPromotionRequestManager(promotionRequestDataManager);
    const eventDataManager = new EventDataManager({ appState: singleton.appState });
    mobxSingleton.dataManagerFactory.setEventDataManager(eventDataManager);
    const jobDataManager = new JobDataManager({ appState: singleton.appState });
    mobxSingleton.dataManagerFactory.setJobDataManager(jobDataManager);
    const payHistoryDataManager = new PayHistoryDataManager({ appState: singleton.appState });
    mobxSingleton.dataManagerFactory.setPayHistoryDataManager(payHistoryDataManager);
    const configurationDataManager = new ConfigurationDataManager({ appState: singleton.appState });
    mobxSingleton.dataManagerFactory.setConfigurationDataManager(configurationDataManager);
    const teamCycleManager = new TeamCycleManager({ appState: singleton.appState });
    mobxSingleton.dataManagerFactory.setTeamCycleManager(teamCycleManager);
    const diagnosticsManager = new DiagnosticsManager({appState: singleton.appState});
    mobxSingleton.dataManagerFactory.setDiagnosticsManager(diagnosticsManager);
    const salesForceDataManager = new SalesForceDataManager({appState: singleton.appState});
    salesForceDataManager.setLabBaseUrl(labServiceUrl);
    mobxSingleton.dataManagerFactory.setSalesForceDataManager(salesForceDataManager);
    mobxSingleton.dataManagerFactory.getSalesForceDataManager().setMasterAccountId(masterAccountId);
    const accountDataManager = new AccountDataManager({appState: singleton.appState});
    mobxSingleton.dataManagerFactory.setAccountDataManager(accountDataManager);
    const budgetServiceManager = new BudgetServiceManager({appState: singleton.appState});
    mobxSingleton.dataManagerFactory.setBudgetServiceManager(budgetServiceManager);
    mobxSingleton.dataManagerFactory.setUserDataManager(userDataManager);
    const notificationManager = new NotificationDataManager({appState: singleton.appState});
    notificationManager.setBaseUrl(notificationServiceUrl ?? '');
    mobxSingleton.dataManagerFactory.setNotificationDataManager(notificationManager);
    const printServiceDataManager = new PrintServiceDataManager({appState: singleton.appState});
    mobxSingleton.dataManagerFactory.setPrintServiceDataManager(printServiceDataManager);

    const employee = await mobxSingleton.dataManagerFactory.getEmployeeDataManager().getPublicEmployeeByUserId(
        masterAccountId, userInfo.userId);

    if (employee) {
        mobxUser.setEmployeeSourceId(employee.employeeSourceId);
        // We don't load up manager store on page load on the main page so using this flag below
        // to determine whether or not to show the teams tab in the header.
        mobxUser.setHasDirectReports((employee?.directReportsCount ?? 0) > 0);
    }
    mobxUser.setName(userInfo.userDisplayName, userInfo.userFirstName);

    try {
        await mobxSingleton.setupMobxTeamStore(
            mobxUser,
            configurationDataManager,
            masterAccountId,
            userId,
            user.access_token);
    } catch (e) {
        teamLogger.trackException(new Error(e));
        // Configuration calls only check that the user has access to the master account
        // and that the master account has a Team:Pay subscription. If those two checks
        // fail, then the whole account isn't going to work.
        if (e === httpResponseConstants.Forbidden) {
            renderApp(<GlobalErrorView />, null, history);
            history.push(TeamRoutes.InvalidAccount);
            return;
        }

        renderApp(<GlobalErrorView store={mobxSingleton.mobxTeamStore} />, null, history);
        history.push(TeamRoutes.Error);
        return;
    }

    mobxSingleton.mobxTeamStore.setAppConfiguration(appConfiguration);

    // managerView no MST refactor
    mobxSingleton.init(
        mobxSingleton.mobxTeamStore.configuration,
        mobxSingleton.mobxTeamStore.decisionStore,
        mobxSingleton.mobxTeamStore.navigationStore,
        mobxSingleton.mobxTeamStore.user,
        masterAccountId);
    actions.init(mobxSingleton.configurationsStore,
        mobxSingleton.workflowSettingsViewStore,
        mobxSingleton.budgetConfigurationViewStore);

    managerActions.init(mobxSingleton.mobxTeamStore.decisionStore);
    // tslint:disable-next-line: no-floating-promises Don't need to block on this call
    mobxSingleton.workflowSettingsViewStore.initData();

    await mobxSingleton.mobxTeamStore.navigationStore.syncHistoryWithRouterStore(history);

    mobxSingleton.notificationStore.setCycleId(mobxSingleton.configurationsStore.currentConvertedConfiguration.cycleId);
    await mobxSingleton.notificationStore.fetchNotificationSummary();

    // pull invalid employee data if users is an hr admin / support,
    // the feature flag is on, and you are not loading into the data summary page.
    // The data summary page will load the invalid data itself otherwise there is a race
    // condition that will be created.
    if (mobxSingleton.mobxTeamStore.user.isHrAdmin &&
        featureFlags[FeatureFlags.ShowDataSummary] &&
        !history.location.search.includes('DataSummary')) {
        // Do not await this since it's not vital to using the app
        mobxSingleton.teamErrorData.requestEmployeeErrorData(false);
    }

    mobxSingleton.mobxTeamStore.redirectToLandingPage();

    renderApp(<Team store={mobxSingleton.mobxTeamStore} />, mobxSingleton.mobxTeamStore, history);

    setInterval((): void | Promise<void> => {
        const eventStore = mobxSingleton.eventStore;
        if (eventStore.employeeEvents.length > 0) {
            return eventStore.sendEvents();
        }
    }, 5000);
};

const logout = (): Promise<{}> => {
    return openIdAuthentication.signOut();
};

// tslint:disable-next-line:no-floating-promises
bootApp();